<template>
  <div class="col-12 pa-0 claim-detail-wrapper">
    <v-row v-if="question?.isClaimFormInvalid && !this.question.Value.length">
      <v-col>
        <v-alert dense text type="error" icon="mdi-alert-circle" class="mb-4">
          <span class="black--text">
            Please add your details of your claim below.
          </span>
        </v-alert>
      </v-col>
    </v-row>
    <div
      class="white--text d-flex justify-space-between font-16 font-weight-bold claim-header"
    >
      <div class="claim-detail-text">Claim details</div>
    </div>
    <div
      class="d-flex flex-column flex-lg-row justify-space-between layoutTableBuilder"
    >
      <v-form ref="form" v-model="isFormValid">
        <v-row class="table-row">
          <v-col
            class="col-sm-12"
            v-for="(item, index) in question.Questions"
            :key="index"
          >
            <component
              :is="item.AnswerControlType"
              :question="item"
            ></component>
          </v-col>
          <v-col class="col-sm-12">
            <v-btn
              depressed
              rounded
              color="#106175"
              large
              height="48px"
              width="100%"
              data-uiid="AddItemButton - Previous Claim"
              :class="$vuetify.breakpoint.mdAndUp ? 'button-max-width' : ''"
              @click="add"
              aria-label="Add"
              ><span class="font-20 font-weight-bold white--text"
                >Add</span
              ></v-btn
            >
          </v-col>
        </v-row>
      </v-form>
    </div>
    <template v-if="question.Value.length">
      <TableBase :question="question" v-if="$vuetify.breakpoint.mdAndUp" />
      <template v-else>
        <div
          class="possession-container mb-4"
          v-for="(item, index) in question.Value"
          :key="index"
        >
          <div class="border-r10 pa-3 mt-5">
            <div class="d-flex justify-space-between">
              <div
                class="d-flex flex-grow-1 justify-space-between align-center"
              >
                <div class="item-title font-12 pa-1 px-4">
                  {{ item.PreviousClaimClaimedUnder.Value.Text }}
                </div>
                <h4>
                  {{ item.PreviousClaimDateOfLoss.Value }}
                </h4>
              </div>
              <div class="d-none d-lg-flex">
                <BaseButton
                  text="REMOVE"
                  flat
                  rounded
                  small
                  icon="mdi-minus-circle"
                  class="font-14"
                  :link="true"
                  @click="question.Value.splice(index, 1)"
                  :iconColor="'var(--red-500)'"
                />
              </div>
            </div>
            <div
              class="d-flex py-4 justify-space-between font-weight-bold font-20 description__wrapper"
            >
              <h4 class="mr-6">
                {{ item.PreviousClaimTypeOfLoss.Value.Text }}
              </h4>
              <h4>
                {{
                  item.PreviousClaimAmountOfLoss.Value
                    | formatCurrency(AMOUNT_DEFAULT_FORMAT)
                }}
              </h4>
            </div>
          </div>
          <div class="justify-center d-flex d-lg-none">
            <BaseButton
              text="REMOVE"
              flat
              rounded
              small
              icon="mdi-minus-circle"
              class="font-14 mt-5"
              @click="question.Value.splice(index, 1)"
              :iconColor="'var(--red-500)'"
              link
            />
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import InputBase from "@/components/Inputs/InputBase";
import InputMask from "@/components/Inputs/InputMask";
import QuestionDate from "@/components/Questions/QuestionDate";
import SelectBase from "@/components/Selects/SelectBase";
import TableBase from "@/components/Tables/TableBase";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import BaseButton from "@/components/BaseComponents/BaseButton";
import { AMOUNT_DEFAULT_FORMAT } from "@/constants/currency-format.constants";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "LayoutTableBuilder",
  props: {
    question: Object,
  },
  components: {
    InputBase,
    InputMask,
    SelectBase,
    TableBase,
    QuestionDate,
    BaseButton,
  },
  data() {
    return {
      isFormValid: false,
      AMOUNT_DEFAULT_FORMAT,
    };
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, ["increaseRepeatingQuestionSetIndex"]),
    add() {
      if (!this.$refs.form.validate()) {
        this.$refs.form.$el.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
        return;
      }
      // this function creates a copy of the Questions array (not reactive)
      // using all the original properties and adding the RepeatingQuestionSetIndex property
      const setUserEntriesData = this.question.Questions.map((item) => {
        return {
          ...item,
          RepeatingQuestionSetIndex: this.repeatingQuestionSetIndex,
        };
      });

      // Vuetify data tables will only accept array of objects
      // this function returns an object of nested objects
      // we create object keys from the InterfaceKey values and assign the current item in the reduce loop (an object) to each key
      // each object is then put inside a wrapping object which is the returned accumulator value
      const setTableRowData = setUserEntriesData.reduce((acc, item) => {
        acc[item.InterfaceKey] = {
          ...item,
        };
        return acc;
      }, {});

      // reset form validation
      this.$parent.$parent.resetValidation();

      this.question.Value.push(setTableRowData);
      this.$refs.form.reset();

      // this action reset input applying moneny format with v-mask (which can't be reset together with `this.$refs.form`)
      this.$refs.form.$el
        ?.querySelectorAll("input[data-uiid*='_MoneyFormat']")
        .forEach((input) => {
          input.value = "";
        });

      // this action increases the repeatingQuestionSetIndex by 1
      this.increaseRepeatingQuestionSetIndex();
    },
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, ["repeatingQuestionSetIndex"]),
  },
};
</script>

<style lang="scss" scoped>
td {
  font-weight: bold;
}

th {
  font-weight: 500;
  color: black !important;
  font-size: 14px !important;
}

.button-max-width {
  max-width: 226px;
}

.table-row {
  .col {
    margin-bottom: 5px;

    :deep(.v-input) {
      .v-input__control {
        .v-input__slot {
          margin-bottom: 0px;
        }
        .v-text-field__details {
          background-color: #f3f3f3;
          margin-bottom: 0;
          padding-top: 4px;
          min-height: 18px;
        }
      }
    }
    :deep(.d2c-date-input) {
      .invisible-input {
        .v-input__control {
          .v-text-field__details {
            margin-top: 0px;
            padding-top: 4px;
          }
        }
      }
    }
    :deep(p) {
      font-size: 16px !important;
    }
    :deep(.date-container) {
      .day-input {
        p {
          font-size: 14px !important;
        }
      }
      .month-input {
        p {
          font-size: 14px !important;
        }
      }
      .year-input {
        p {
          font-size: 14px !important;
        }
      }
    }
  }

  :last-child.col {
    padding-top: 0;
    margin-top: 3px;
  }
}

.layoutTableBuilder {
  background-color: #f3f3f3 !important;
  padding: 19px 35px 24px 35px !important;
}

.claim-header {
  background-color: #354048;
  padding: 10px 0px 10px 24.8px;
}

.claim-detail-text {
  width: 100%;
  flex-grow: 0;
  margin: 0 0px 0 0;
  object-fit: contain;
  font-family: Montserrat;
  font-size: 16px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.4px;
  text-align: left;
  color: #fff;
}

.item-title {
  background-color: #007185;
  color: white;
  font-weight: bold;
  border-radius: 17px;
  width: fit-content;
}

.border-r10 {
  border-radius: 10px;
  border: solid 1px #b4b4b4;
}

@media screen and (max-width: $grid-breakpoints-sm) {
  .layoutTableBuilder {
    padding: 19px 22px 16px 22px !important;
    margin: 0 -8px;
  }
  .claim-header {
    margin: 0 -8px;
  }
  .table-row {
    :last-child(.col) {
      margin-top: 31px;
    }
  }
}
</style>
